import classNames from "classnames";
import { debounce, get } from "lodash";
import { FC, useCallback, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import Divider from "~/components/Divider";
import Icon from "~/components/Icon";
import ChangeVariantModal from "~/components/Modals/ChangeVariantModal";
import PreviewableImage from "~/components/PreviewableImage";
import Quantity from "~/components/Quantity";
import Text from "~/components/Text";
import Colors from "~/constants/colors";
import { PATH } from "~/constants/enum";
import { useCart } from "~/data/cart";
import { CartAtom, CartItem as ICartItem } from "~/services/cart";
import { ProductVariant } from "~/services/product";
import {
  trackingOnProductClick,
  trackingQuantityChange,
  trackingRemoveItemFromCart,
} from "~/services/tracking";
import styles from "./CartItem.module.scss";
import { getUrlCustom } from "~/utils/theme";
import EditIcon from "~/assets/svgs/edit-2.svg";
import TrashIcon from "~/assets/svgs/trash.svg";
import StatusIcon from "~/assets/svgs/status.svg";

interface CartItemProps {
  cartItem: ICartItem;
  checked?: boolean;
  onChecked?: () => void;
  loadingRemoveCartItem?: boolean;
  loading?: boolean;
}

const renderTrash = (loading: boolean, onClick: () => void) => {
  if (!!loading) {
    return <StatusIcon className="animate-spin size-6" />;
  }

  return (
    <TrashIcon
      className=" cursor-pointer size-6"
      color={Colors.GREY_300}
      onClick={onClick}
    />
  );
};

const CartItem: FC<CartItemProps> = ({ cartItem, loading }) => {
  const [cart, setCart] = useRecoilState(CartAtom.cart);
  const [openChangeVariantModal, setOpenChangeVariantModal] = useState(false);

  const {
    updateCartItems,
    removeCartItem,
    addToCart,
    loadingAddToCart,
    loadingUpdateCartItems,
    loadingRemoveCartItem,
  } = useCart();

  const productNoteColor = useMemo(() => {
    const note = cartItem?.productNote?.find(
      (note: any) => note.type === "color"
    );
    if (note) {
      return [{ label: note.value }];
    } else return undefined;
  }, [cartItem?.productNote]);

  const debounceUpdateCartItem = useCallback(
    debounce(
      (cartItemUId: string, quantity: number) =>
        updateCartItems({
          variables: {
            cartId: cart.cartId,
            cartItemUId,
            quantity,
          },
        }),
      500
    ),
    []
  );

  const handleChangeQuantityCartItem = (
    cartItemUId: string,
    quantity: number
  ) => {
    setCart((prevCart) => ({
      ...prevCart,
      items: prevCart.items?.map((cartItem) => {
        if (cartItem.uid === cartItemUId) {
          trackingQuantityChange({
            quantity,
            variant: cartItem.selectedVariant,
          });
          return { ...cartItem, quantity };
        }

        return cartItem;
      }),
    }));
    debounceUpdateCartItem(cartItemUId, quantity);
  };

  const handleDelete = async () => {
    if (!cartItem) return;
    trackingRemoveItemFromCart(cartItem?.id as any);
    // setCart((prevCart) => ({
    //   ...prevCart,
    //   items: prevCart.items?.filter(
    //     (cartItem) => cartItem.id !== cartItemId
    //   ),
    // }));
    return await removeCartItem({
      variables: {
        cartId: cart.cartId,
        cartItemId: cartItem.id,
      },
    });
  };

  const totalPrice = useMemo(() => {
    const price =
      cartItem.selectedVariant?.product?.price_range.minimum_price?.final_price
        ?.value || 0;
    return price * cartItem.quantity;
  }, [
    cartItem.quantity,
    cartItem.selectedVariant?.product?.price_range.minimum_price?.final_price
      ?.value,
  ]);

  const onChangeProductVariant = useCallback(
    async (
      {
        selectedVariant,
        productNote,
      }: {
        selectedVariant: Partial<ProductVariant>;
        productNote: { name: string; type: string; value: string }[];
      },
      parentSKU: string
    ) => {
      if (
        selectedVariant?.product?.sku === cartItem.selectedVariant?.product?.sku
      ) {
        await updateCartItems({
          variables: {
            cartId: cart.cartId,
            cartItemUId: cartItem.uid,
            quantity: cartItem.quantity,
            productNote,
            openCart: true,
          },
        });
      } else {
        await addToCart({
          variables: {
            sku: selectedVariant?.product?.sku,
            quantity: cartItem.quantity,
            cartId: cart.cartId as any,
            parentSku: parentSKU,
            productNote,
          },
        });
        await handleDelete();
      }
      // toast.success("updated successfully");
      setOpenChangeVariantModal(false);
    },
    [cart]
  );

  return (
    <div className={styles.cart_item}>
      {/* <Checkbox checked={checked} onChange={onChecked} /> */}
      {!loading ? (
        <img
          src={cartItem.product?.image?.url || ""}
          alt={cartItem.product?.name || ""}
          width={96}
          height={96}
          className={styles.image}
        />
      ) : (
        <Skeleton className="aspect-square" width={96} height={96} />
      )}
      <div className={styles.information}>
        {!loading ? (
          <div className={styles.product}>
            <div className="flex w-full justify-between lg:justify-center gap-1">
              <a
                className="flex-1"
                href={getUrlCustom({
                  pathname: PATH.ProductDetail,
                  query: {
                    variant: cartItem.selectedVariant?.product?.id,
                    slug: cartItem.product?.slug,
                  },
                })}
                onClick={() => {
                  trackingOnProductClick(cartItem.selectedVariant);
                }}
              >
                <Text
                  type="title-5"
                  color={Colors.GREY_900}
                  className="line-clamp-2"
                >
                  {cartItem.product?.name}
                </Text>
              </a>
              <span className="lg:hidden">
                {renderTrash(loadingRemoveCartItem, handleDelete)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Text
                type="body-3"
                color={Colors.GREY_700}
                className={classNames(styles.category, "capitalize")}
              >
                {[
                  ...(productNoteColor ?? []),
                  ...(cartItem.selectedVariant?.attributes ?? []),
                ]
                  ?.map((attr) => attr.label)
                  .join(" / ")}
              </Text>
              <EditIcon
                className="cursor-pointer size-4"
                color={Colors.GREY_400}
                onClick={() => setOpenChangeVariantModal(true)}
              />
            </div>
            {!!cartItem?.productNote.filter(
              (note: any) => note.type !== "color"
            )?.length && (
              <div className="flex flex-wrap">
                {" "}
                {cartItem?.productNote
                  ?.filter((note: any) => note.type !== "color")
                  ?.map((note: any, index: number) => (
                    <div
                      key={`${note.name}-${index}`}
                      className="flex flex-nowrap items-center"
                    >
                      <Text type="body-3" color={Colors.GREY_700}>
                        <b>{note.name}</b>:{" "}
                        {note.type !== "image" ? (
                          note.value
                        ) : (
                          <PreviewableImage
                            src={note.value}
                            alt={note.name}
                            width={24}
                            height={24}
                            className="inline-block"
                          />
                        )}
                      </Text>
                      <span className="mx-1">
                        {index === cartItem?.productNote.length - 1
                          ? null
                          : "|"}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <Skeleton className=""></Skeleton>
            <Skeleton width={"80%"}></Skeleton>
            <Skeleton className="mt-2" width={"60%"}></Skeleton>
          </div>
        )}
        {!loading ? (
          <div className="flex w-full justify-between lg:justify-center ">
            <div className={styles.price_container}>
              <Text
                type="title-4"
                color={Colors.PRIMARY}
                className={styles.price}
              >
                {get(
                  cartItem,
                  "selectedVariant.product.price_range.minimum_price.final_price.symbol",
                  "$"
                )}
                {cartItem.selectedVariant?.product?.price_range.minimum_price?.final_price?.value?.toFixed(
                  2
                )}
              </Text>
              {cartItem.selectedVariant?.product?.price_range.minimum_price
                ?.discount && (
                <Text
                  type="body-3"
                  className={styles.discount_price}
                  color={Colors.GREY_400}
                >
                  {get(
                    cartItem,
                    "selectedVariant.product.price_range.minimum_price.regular_price.symbol",
                    "$"
                  )}
                  {cartItem.selectedVariant?.product?.price_range?.minimum_price?.regular_price?.value?.toFixed(
                    2
                  )}
                </Text>
              )}
            </div>
            <div className="lg:hidden">
              <Quantity
                value={cartItem.quantity}
                onChange={(value) =>
                  handleChangeQuantityCartItem(cartItem.uid || "", value)
                }
                maxValue={cartItem.product?.quantity}
              />
            </div>
          </div>
        ) : (
          <div className="text-center flex justify-between">
            <Skeleton width={70}></Skeleton>
            <Skeleton
              className="lg:hidden"
              height={32}
              width={"7.5rem"}
            ></Skeleton>
          </div>
        )}
        <div className={classNames("hidden lg:block")}>
          {!loading ? (
            <Quantity
              value={cartItem.quantity}
              onChange={(value) =>
                handleChangeQuantityCartItem(cartItem.uid || "", value)
              }
              maxValue={cartItem.product?.quantity}
            />
          ) : (
            <Skeleton height={32} width={"7.5rem"}></Skeleton>
          )}
        </div>
        <Divider
          dividerColor={Colors.GREY_50}
          dividerClassName="block lg:hidden"
        />
        <div className={styles.total_price_container}>
          <Text
            type="body-4"
            color={Colors.GREY_300}
            className="block lg:hidden"
          >
            Total price:
          </Text>
          {!loading && !loadingUpdateCartItems ? (
            <Text
              type="title-4"
              color={Colors.PRIMARY}
              className={styles.total_price}
            >
              ${totalPrice.toFixed(2)}
            </Text>
          ) : (
            <Skeleton width={32}></Skeleton>
          )}
        </div>
        <div className={classNames(styles.icon, "hidden lg:block")}>
          <span className="flex w-full items-center justify-center">
            {!loading ? (
              renderTrash(loadingRemoveCartItem, handleDelete)
            ) : (
              <Skeleton width={20} height={20}></Skeleton>
            )}{" "}
          </span>
        </div>
      </div>
      <ChangeVariantModal
        open={openChangeVariantModal}
        product={cartItem.product}
        loading={
          loadingAddToCart || loadingUpdateCartItems || loadingRemoveCartItem
        }
        defaultVariant={cartItem.selectedVariant}
        onConfirm={onChangeProductVariant}
        defaultProductNote={cartItem.productNote}
        onCancel={() => setOpenChangeVariantModal(false)}
      />
    </div>
  );
};

export default CartItem;
